import * as React from 'react';
import { useCallback } from 'react';

import * as moment from 'moment';
import DatePicker from 'react-datepicker';
import { type WrappedFieldProps } from 'redux-form';

import { dateRangePopperStyle } from '~/components/DateTimePicker/DateTimePicker.styled';
import FormError from '~/components/FormError';
import Input from '~/components/Input';
import { type LabelProps } from '~/components/Label/Label';
import { Keys } from '~/constants/keys';

interface DateTimePickerProps {
  // Applies style classes to inner input element
  className?: string;
  dataAnalytics?: string;
  dataPendo?: string;
  dataTestid?: string;
  dateLimit?: Date | moment.Moment;
  disablePastDates?: boolean;
  disableWeekends?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  hasTime?: boolean;
  id?: string;
  input?: JSX.Element;
  invalid?: boolean;
  label?: string;
  labelProps?: LabelProps;
  minDate?: Date | moment.Moment;
  name?: string;
  onChange: (date: moment.Moment | string) => void;
  placeholder?: string;
  tabIndex?: number;
  value?: Date | moment.Moment;
  wrapperClassName?: string;
}

interface DateRangePickerProps
  extends Omit<DateTimePickerProps, 'dataTestid' | 'onChange' | 'value'> {
  onChange: (dates: [Date | null, Date | null] | null) => void;
  value: [Date | undefined, Date | undefined];
}

const DateTimePickerInput = (props: DateRangePickerProps | DateTimePickerProps) => (
  <Input
    {...props}
    autoComplete="off"
    errorMessage={undefined} // Do not set errorMessage prop for Input since error message rendering is handled in DateTimePicker
    onChange={undefined} // Do not set onChange since the text box (Input) should be read only
    readOnly
    value={props.value?.toString()}
  />
);

const DateTimePicker = (props: DateTimePickerProps) => {
  const isValidDate = React.useCallback(
    (date: Date) => {
      const current = moment(date);
      if (props.disableWeekends && (current.day() === 0 || current.day() === 6)) return false;
      return true;
    },
    [props.disableWeekends],
  );

  const renderErrors = React.useCallback(() => {
    if (props.errorMessage) return <FormError>{props.errorMessage}</FormError>;
  }, [props.errorMessage]);

  // Basically a dummy onChange to keep backwards compat with usages that rely on moment functions
  const onChange = React.useCallback(
    (date: Date) => {
      const momentDate = moment(date);
      return props.onChange(momentDate);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange],
  );

  // This check is because the UPS carrier form is passing an invalid moment date
  const isValidValue = React.useMemo(() => moment(props.value).isValid(), [props.value]);
  const momentDate = React.useMemo(
    () => (isValidValue ? moment(props.value) : moment()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.value],
  );

  const onKeyDown = useCallback((e) => {
    if (e.key === Keys.Tab) e.stopPropagation();
  }, []);

  return (
    <div>
      <DatePicker
        autoComplete="off"
        className={props.className}
        customInput={props.input || <DateTimePickerInput {...props} />}
        disabled={props.disabled}
        filterDate={isValidDate}
        id={props.id}
        maxDate={props.dateLimit ? moment(props.dateLimit).toDate() : null}
        minDate={
          props.disablePastDates
            ? new Date()
            : props.minDate
            ? moment(props.minDate).toDate()
            : null
        }
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholderText={props.placeholder}
        popperClassName={dateRangePopperStyle as unknown as string}
        selected={isValidValue ? momentDate.toDate() : undefined} // no selection if value is invalid
        showTimeSelect={props.hasTime}
        wrapperClassName={props.wrapperClassName}
      />
      {renderErrors()}
    </div>
  );
};

const DateRangePicker = (props: DateRangePickerProps) => {
  const isValidDate = React.useCallback(
    (date: Date) => {
      const current = moment(date);
      if (props.disableWeekends && (current.day() === 0 || current.day() === 6)) return false;
      return true;
    },
    [props.disableWeekends],
  );

  const formatWeekDay = React.useCallback((name) => name.substr(0, 1), []);

  const renderErrors = () => {
    if (props.errorMessage) return <FormError>{props.errorMessage}</FormError>;
  };

  // Basically a dummy onChange to keep backwards compat with usages that rely on moment functions
  const onChange = React.useCallback(
    (dates) => props.onChange(dates),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange],
  );

  const [startDate, endDate] = props.value;
  const onKeyDown = useCallback((e) => {
    if (e.key === Keys.Tab) e.stopPropagation();
  }, []);
  return (
    <>
      <DatePicker
        className={props.className}
        customInput={props.input || <DateTimePickerInput {...props} />}
        disabled={props.disabled}
        endDate={endDate}
        filterDate={isValidDate}
        formatWeekDay={formatWeekDay}
        id={props.id}
        maxDate={props.dateLimit ? moment(props.dateLimit).toDate() : null}
        minDate={
          props.disablePastDates
            ? new Date()
            : props.minDate
            ? moment(props.minDate).toDate()
            : null
        }
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholderText={props.placeholder}
        popperClassName={dateRangePopperStyle as unknown as string}
        selected={startDate}
        selectsRange
        showTimeSelect={props.hasTime}
        startDate={startDate}
        wrapperClassName={props.wrapperClassName}
      />
      {renderErrors()}
    </>
  );
};

const DateTimePickerField = (props: Partial<DateTimePickerProps> & WrappedFieldProps) => {
  const { input, meta } = props;
  const invalid = meta && meta.touched && meta.error;
  const errorMessage = invalid ? meta.error : null;

  const requiredDatePickerProperties = {
    dateLimit: props.dateLimit,
    disablePastDates: props.disablePastDates,
    disableWeekends: props.disableWeekends,
    errorMessage,
    id: props.id,
    label: props.label,
    labelProps: props.labelProps,
    name: props.name,
  };

  // Keep the store serializable.
  const date = React.useMemo(() => moment(input.value), [input.value]);
  const onDateChange = React.useCallback(
    (date: Date | moment.Moment | string) => {
      input.onChange(date.toString());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.onChange],
  );

  return (
    <DateTimePicker
      onChange={onDateChange}
      value={date}
      {...requiredDatePickerProperties}
      dataAnalytics={props.dataAnalytics}
      dataPendo={props.dataPendo}
      dataTestid={props.dataTestid}
      invalid={invalid}
      tabIndex={props.tabIndex}
    />
  );
};

export default DateTimePicker;
export { DateRangePicker, DateTimePickerField };
